import Navbar from "@/components/layout/Navbar";
import Footer from "@/components/layout/Footer";
import ScrollToTop from "@/components/layout/ScrollToTop";
import motion from "@/lib/lazy-motion-tags";
import { Helmet } from "react-helmet";
import IconLoader from "@/components/IconLoader";
import { Card } from "@/components/ui/card";

const About = () => {
  const values = [
    {
      iconName: "Target",
      title: "الدقة والإتقان",
      description: "نؤمن أن الفارق هو في التفاصيل الصغيرة. نحن ملتزمون بالدقة في كل خطوة من خطوات التصنيع.",
    },
    {
      iconName: "Lightbulb",
      title: "الابتكار",
      description: "نواكب أحدث التقنيات والتصاميم العالمية لنلبي باستمرار متطلبات السوق.",
    },
    {
      iconName: "Heart",
      title: "الاستدامة",
      description: "نستخدم خامات طبيعية آمنة ونساهم في تقليل الهدر للاستثمار في مستقبل أكثر استدامة.",
    },
    {
      iconName: "Users",
      title: "الراحة",
      description: "نحرص على توفير تجربة سلسة من البداية للنهاية - من التشاور والطلب إلى التركيب السريع.",
    },
    {
      iconName: "Zap",
      title: "المرونة",
      description: "نقدم حلول مخصصة تلبي احتياجات كل مشروع، سواء كان منزل صغير أو مشروع عقاري ضخم.",
    },
    {
      iconName: "Star",
      title: "التميز",
      description: "كل منتج نصنعه يحمل لمسة تصميمية فريدة وعناية فائقة بالتفاصيل.",
    },
  ];

  const reasons = [
    "خبرة موثوقة في قطاع البناء والتشطيبات",
    "جودة مضمونة - تخضع منتجاتنا لاختبارات صارمة",
    "خدمة متميزة - فريق دعم جاهز للإجابة على استفساراتك",
    "أسعار تنافسية - أفضل قيمة مقابل المال في السوق",
    "ضمان شامل - نقف خلف جودة كل منتجاتنا",
  ];

  return (
    <>
      <ScrollToTop />
      <Helmet>
        <title>من نحن - New Business | رواد صناعة أبواب WPC</title>
        <meta name="description" content="تعرف على New Business، رواد صناعة أبواب WPC في السعودية مع خبرة تمتد لأكثر من 5 سنوات" />
        <meta name="keywords" content="من نحن, New Business, أبواب WPC, صناعة أبواب, الرياض, خبرة 5 سنوات, جودة مضمونة" />
        <meta property="og:title" content="من نحن - New Business | رواد صناعة أبواب WPC" />
        <meta property="og:description" content="تعرف على New Business، رواد صناعة أبواب WPC في السعودية مع خبرة تمتد لأكثر من 5 سنوات" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.newbusiness.com.sa/about" />
        <link rel="canonical" href="https://www.newbusiness.com.sa/about" />
      </Helmet>
      
      <div className="min-h-screen">
        <Navbar />
        
        {/* Hero */}
        <section className="pt-32 pb-16 bg-gradient-to-br from-background via-muted/30 to-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="text-center max-w-4xl mx-auto"
            >
              <h1 className="text-5xl md:text-6xl font-bold mb-6">
                من <span className="text-gradient">نحن</span>
              </h1>
              <p className="text-xl text-muted-foreground">
                نعيد تعريف صناعة الأبواب من خلال الابتكار والجودة
              </p>
            </motion.div>
          </div>
        </section>

        {/* Mission */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
              >
                <h2 className="text-4xl font-bold mb-6">رسالتنا</h2>
                <p className="text-lg text-muted-foreground leading-relaxed mb-6">
                  نحن نعيد تعريف صناعة الأبواب. الباب ليس مجرد قطعة خشبية تفصل بين الغرف، بل هو عنصر أساسي يعكس ذوقك، ويحمي راحتك، ويضمن خصوصيتك.
                </p>
                <p className="text-lg text-muted-foreground leading-relaxed">
                  تعتمد New Business على تقنية الخشب المركب (WPC) من خلال حلول مبتكرة ومتطورة تجمع بين الجمال والمتانة والاستدامة.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-gradient-primary rounded-2xl p-12 shadow-glow"
              >
                <h3 className="text-3xl font-bold text-accent mb-6">رؤيتنا</h3>
                <p className="text-lg text-accent leading-relaxed">
                  أن نصبح الخيار الأول لكل من يبحث عن أبواب داخلية عالية الجودة في السوق السعودي والخليجي، مع الالتزام المستمر بأعلى معايير الجودة والابتكار.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Values */}
        <section className="py-24 bg-background">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl font-bold mb-6">
                القيم الأساسية التي <span className="text-gradient">نلتزم بها</span>
              </h2>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {values.map((value, index) => {
                const Icon = value.icon;
                return (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <Card className="p-8 hover-lift h-full">
                      <div className="w-16 h-16 rounded-2xl bg-gradient-primary flex items-center justify-center mb-6">
                        <IconLoader name={value.iconName} className="h-8 w-8 text-accent" />
                      </div>
                      <h3 className="text-2xl font-bold mb-4">{value.title}</h3>
                      <p className="text-muted-foreground leading-relaxed">{value.description}</p>
                    </Card>
                  </motion.div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Why Trust Us */}
        <section className="py-24 bg-card">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center max-w-4xl mx-auto"
            >
              <h2 className="text-4xl font-bold mb-12">لماذا يثق بنا العملاء؟</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-right">
                {reasons.map((reason, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.6, delay: index * 0.1 }}
                    viewport={{ once: true }}
                    className="flex items-start gap-4 p-6 rounded-xl bg-background/50 hover:bg-background transition-colors"
                  >
                    <div className="flex-shrink-0 w-2 h-2 rounded-full bg-primary mt-2" />
                    <p className="text-lg font-medium">{reason}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        <Footer />
      </div>
    </>
  );
};

export default About;
